import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Receipt, Globe2, Wallet, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['hotel', 'flight', 'transport', 'guide', 'visa', 'activity', 'marketing', 'other'];
const PAYMENT_STATUSES = ['pending', 'paid', 'partial'];

// BATCH 6C Part 3 — must exactly match backend PAID_VIA_OPTIONS (routes/accounting.py).
const PAID_VIA_OPTIONS = [
  { value: 'company_bank_transfer', label: 'Company Bank Transfer' },
  { value: 'company_card', label: 'Company Card' },
  { value: 'vendor_advance_balance', label: 'Vendor Advance Balance' },
  { value: 'personal_card_reimbursable', label: 'Personal Card — Reimbursable' },
];
const PAID_VIA_LABELS = Object.fromEntries(PAID_VIA_OPTIONS.map((o) => [o.value, o.label]));

// Vendor Payment Gateway Charges (2026-09) — must match backend
// VENDOR_PAYMENT_METHODS (routes/accounting.py).
const VENDOR_PAYMENT_METHODS = [
  { value: 'personal_card', label: 'Personal Card' },
  { value: 'corporate_card', label: 'Corporate Card' },
  { value: 'neft', label: 'NEFT' },
  { value: 'imps', label: 'IMPS' },
];

const EMPTY_EXPENSE = {
  tripId: '', vendorId: '', vendorName: '', description: '', amount: '', gstAmount: '0', tcsAmount: '0',
  paidAmount: '0', dueDate: '', category: 'other', paymentStatus: 'pending', paymentDate: '',
  invoiceNumber: '', notes: '', paymentAccountId: '',
  // BATCH 6C Part 3 — how the vendor was actually paid.
  paidVia: '', reimbursedBy: '', cardLast4: '',
  // BATCH 6C Part 2 — vendor FX remittance (paying a foreign-currency vendor invoice).
  foreignInvoiceAmount: '', foreignCurrency: '', remittanceFxRate: '', remittanceRateDate: '', remittanceFee: '',
  // Vendor Payment Gateway Charges (2026-09) — domestic card/NEFT/IMPS fee.
  vendorPaymentMethod: '',
};

const statusColors = { pending: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', partial: 'bg-blue-100 text-blue-700' };

const AccountingExpenses = ({ expenses, vendors, onRefresh }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EXPENSE);
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/api/accounting/bank-accounts`, { withCredentials: true })
      .then((r) => setBankAccounts(r.data || []))
      .catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY_EXPENSE); setEditing(null); setShowDialog(true); };
  const openEdit = (exp) => {
    setForm({
      tripId: exp.tripId || '', vendorId: exp.vendorId || '', vendorName: exp.vendorName || '',
      description: exp.description, amount: String(exp.amount), gstAmount: String(exp.gstAmount || 0),
      tcsAmount: String(exp.tcsAmount || 0),
      paidAmount: String(exp.paidAmount || 0),
      dueDate: exp.dueDate || '',
      category: exp.category || 'other',
      paymentStatus: exp.paymentStatus || 'pending', paymentDate: exp.paymentDate || '',
      invoiceNumber: exp.invoiceNumber || '', notes: exp.notes || '',
      paymentAccountId: exp.paymentAccountId || '',
      paidVia: exp.paidVia || '', reimbursedBy: exp.reimbursedBy || '', cardLast4: exp.cardLast4 || '',
      foreignInvoiceAmount: exp.foreignInvoiceAmount ? String(exp.foreignInvoiceAmount) : '',
      foreignCurrency: exp.foreignCurrency || '',
      remittanceFxRate: exp.remittanceFxRate ? String(exp.remittanceFxRate) : '',
      remittanceRateDate: exp.remittanceRateDate || '',
      remittanceFee: exp.remittanceFee ? String(exp.remittanceFee) : '',
      vendorPaymentMethod: exp.vendorPaymentMethod || '',
    });
    setEditing(exp.expenseId);
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.paidVia === 'personal_card_reimbursable') {
      if (!form.reimbursedBy.trim()) { toast.error("Reimbursed By (person's name) is required for Personal Card"); return; }
      if (!/^\d{4}$/.test(form.cardLast4.trim())) { toast.error('Card Last 4 must be exactly 4 digits'); return; }
    }
    setSaving(true);
    const payload = {
      ...form,
      amount: parseFloat(form.amount) || 0,
      gstAmount: parseFloat(form.gstAmount) || 0,
      tcsAmount: parseFloat(form.tcsAmount) || 0,
      paidAmount: parseFloat(form.paidAmount) || 0,
      foreignInvoiceAmount: parseFloat(form.foreignInvoiceAmount) || 0,
      remittanceFxRate: parseFloat(form.remittanceFxRate) || 0,
      remittanceFee: parseFloat(form.remittanceFee) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/accounting/expenses/${editing}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/accounting/expenses`, payload);
        toast.success('Expense recorded');
      }
      setShowDialog(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally { setSaving(false); }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/expenses/${expenseId}`);
      toast.success('Expense deleted');
      onRefresh();
    } catch (err) { toast.error('Failed to delete expense'); }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalGST = expenses.reduce((sum, e) => sum + (e.gstAmount || 0), 0);
  const pendingCount = expenses.filter(e => e.paymentStatus === 'pending').length;

  // Live preview of BATCH 6C Part 2's fee-inclusive Total INR Debited —
  // mirrors backend's _compute_expense_derived_fields exactly (never folds
  // the fee silently into the base amount, always shown as its own line).
  const foreignAmt = parseFloat(form.foreignInvoiceAmount) || 0;
  const fxRate = parseFloat(form.remittanceFxRate) || 0;
  const remittanceFee = parseFloat(form.remittanceFee) || 0;
  const showFxPreview = foreignAmt > 0 && fxRate > 0;
  const totalInrDebitedPreview = showFxPreview ? Math.round((foreignAmt * fxRate + remittanceFee) * 100) / 100 : 0;

  // Vendor Payment Gateway Charges (2026-09) — live preview mirrors the
  // backend's server-side lookup (never trusts a client-entered %); the
  // real fee is always recomputed on save from the vendor's own stored rate.
  const selectedVendor = vendors.find((v) => v.vendorId === form.vendorId);
  const methodFeePct = form.vendorPaymentMethod ? Number((selectedVendor?.paymentMethodFeeRates || {})[form.vendorPaymentMethod] || 0) : 0;
  const grossPayment = parseFloat(form.paidAmount) || 0;
  const showMethodPreview = !!form.vendorPaymentMethod && grossPayment > 0;
  const methodFeePreview = showMethodPreview ? Math.round(grossPayment * methodFeePct / 100 * 100) / 100 : 0;
  const totalDebitedMethodPreview = showMethodPreview ? Math.round((grossPayment + methodFeePreview) * 100) / 100 : 0;

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="flex justify-between items-center">
        <div className="flex gap-4 text-sm text-slate-500">
          <span>{expenses.length} expense{expenses.length !== 1 ? 's' : ''}</span>
          <span>Total: <strong className="text-slate-900">₹{totalAmount.toLocaleString('en-IN')}</strong></span>
          <span>GST: <strong className="text-teal-700">₹{totalGST.toLocaleString('en-IN')}</strong></span>
          {pendingCount > 0 && <span className="text-amber-600">{pendingCount} pending</span>}
        </div>
        <Button onClick={openNew} data-testid="add-expense-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
          <Plus className="h-4 w-4 mr-2" />Add Expense
        </Button>
      </div>

      {expenses.length === 0 ? (
        <Card className="p-12 text-center">
          <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No expenses recorded yet.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="expenses-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Paid Via</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">GST</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {expenses.map(exp => (
                  <tr key={exp.expenseId} className="hover:bg-slate-50/50 transition-colors" data-testid={`expense-row-${exp.expenseId}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900">{exp.description}</p>
                      {exp.invoiceNumber && <p className="text-[11px] text-slate-400">Inv: {exp.invoiceNumber}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{exp.vendorName || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-slate-500 capitalize">{exp.category}</span>
                    </td>
                    <td className="px-4 py-3" data-testid={`expense-paid-via-${exp.expenseId}`}>
                      {exp.paidVia ? (
                        <div className="space-y-0.5">
                          <span className="text-[11px] font-medium text-slate-600">{PAID_VIA_LABELS[exp.paidVia] || exp.paidVia}</span>
                          {exp.paidVia === 'personal_card_reimbursable' && (
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-[10px] font-mono">•••• {exp.cardLast4}</Badge>
                              <Badge className={`text-[10px] ${exp.reimbursementStatus === 'reimbursed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {exp.reimbursementStatus === 'reimbursed' ? 'Reimbursed' : 'Pending reimb.'}
                              </Badge>
                            </div>
                          )}
                          {exp.totalInrDebited > 0 && (
                            <div className="flex items-center gap-1 text-[10px] text-indigo-600" data-testid={`expense-fx-debited-${exp.expenseId}`}>
                              <Globe2 className="h-3 w-3" /> FX: ₹{Number(exp.totalInrDebited).toLocaleString('en-IN')} debited
                            </div>
                          )}
                          {exp.vendorPaymentMethodFee > 0 && (
                            <div className="flex items-center gap-1 text-[10px] text-indigo-600" data-testid={`expense-method-fee-${exp.expenseId}`}>
                              <CreditCard className="h-3 w-3" /> Fee: ₹{Number(exp.vendorPaymentMethodFee).toLocaleString('en-IN')} ({exp.vendorPaymentMethodFeePct}%)
                            </div>
                          )}
                        </div>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">₹{(exp.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-500">₹{(exp.gstAmount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-slate-900">₹{(exp.totalAmount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusColors[exp.paymentStatus] || statusColors.pending}`}>
                        {exp.paymentStatus || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(exp)}><Pencil className="h-3.5 w-3.5 text-slate-400" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(exp.expenseId)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Expense' : 'Record New Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="expense-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Description *</Label>
                <Input value={form.description} onChange={e => set('description', e.target.value)} required className="mt-1" data-testid="expense-description-input" />
              </div>
              <div>
                <Label>Vendor</Label>
                <Select value={form.vendorId} onValueChange={v => {
                  const vendor = vendors.find(vn => vn.vendorId === v);
                  set('vendorId', v);
                  if (vendor) set('vendorName', vendor.name);
                }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vendor</SelectItem>
                    {vendors.map(v => <SelectItem key={v.vendorId} value={v.vendorId}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => set('category', v)}>
                  <SelectTrigger className="mt-1" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (₹) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} required className="mt-1" data-testid="expense-amount-input" />
              </div>
              <div>
                <Label>GST Amount (₹)</Label>
                <Input type="number" step="0.01" value={form.gstAmount} onChange={e => set('gstAmount', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>TCS Amount (₹)</Label>
                <Input type="number" step="0.01" value={form.tcsAmount} onChange={e => set('tcsAmount', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select value={form.paymentStatus} onValueChange={v => set('paymentStatus', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Paid Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.paidAmount}
                  onChange={e => set('paidAmount', e.target.value)}
                  className="mt-1"
                  data-testid="expense-paid-amount-input"
                  placeholder="0 for unpaid"
                />
              </div>
              <div>
                <Label>Balance Due Date</Label>
                <Input
                  type="date"
                  value={form.dueDate}
                  onChange={e => set('dueDate', e.target.value)}
                  className="mt-1"
                  data-testid="expense-due-date-input"
                />
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input value={form.invoiceNumber} onChange={e => set('invoiceNumber', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Account (optional)</Label>
                <Select value={form.paymentAccountId || 'none'} onValueChange={v => set('paymentAccountId', v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="expense-account-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {bankAccounts.map((a) => (
                      <SelectItem key={a.accountId} value={a.accountId}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Trip ID (optional)</Label>
                <Input value={form.tripId} onChange={e => set('tripId', e.target.value)} className="mt-1" placeholder="Link to a trip" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1" />
              </div>
            </div>

            {/* BATCH 6C Part 3 — Paid Via + conditional Personal Card fields */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1.5 mb-2">
                <Wallet className="h-3.5 w-3.5" /> Paid Via
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Select value={form.paidVia || 'none'} onValueChange={v => set('paidVia', v === 'none' ? '' : v)}>
                    <SelectTrigger className="mt-1" data-testid="expense-paid-via-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {PAID_VIA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.paidVia === 'personal_card_reimbursable' && (
                  <>
                    <div>
                      <Label>Reimbursed By (person's name) *</Label>
                      <Input value={form.reimbursedBy} onChange={e => set('reimbursedBy', e.target.value)} className="mt-1" data-testid="expense-reimbursed-by-input" placeholder="e.g. Priya Sharma" />
                    </div>
                    <div>
                      <Label>Card Last 4 Digits *</Label>
                      <Input
                        value={form.cardLast4}
                        onChange={e => set('cardLast4', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="mt-1 font-mono"
                        data-testid="expense-card-last4-input"
                        placeholder="1234"
                        maxLength={4}
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Last 4 digits only — never store a full card number.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* BATCH 6C Part 2 — Vendor FX Remittance */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1.5 mb-2">
                <Globe2 className="h-3.5 w-3.5" /> Vendor FX Remittance (if paying a foreign-currency vendor invoice)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Foreign Invoice Amount</Label>
                  <Input type="number" step="0.01" value={form.foreignInvoiceAmount} onChange={e => set('foreignInvoiceAmount', e.target.value)} className="mt-1" data-testid="expense-foreign-amount-input" placeholder="e.g. 1000" />
                </div>
                <div>
                  <Label>Foreign Currency</Label>
                  <Input value={form.foreignCurrency} onChange={e => set('foreignCurrency', e.target.value.toUpperCase().slice(0, 4))} className="mt-1" data-testid="expense-foreign-currency-input" placeholder="USD" />
                </div>
                <div>
                  <Label>Remittance FX Rate</Label>
                  <Input type="number" step="0.0001" value={form.remittanceFxRate} onChange={e => set('remittanceFxRate', e.target.value)} className="mt-1" data-testid="expense-remittance-rate-input" placeholder="e.g. 88.50" />
                </div>
                <div>
                  <Label>Rate Date</Label>
                  <Input type="date" value={form.remittanceRateDate} onChange={e => set('remittanceRateDate', e.target.value)} className="mt-1" data-testid="expense-remittance-rate-date-input" />
                </div>
                <div className="col-span-2">
                  <Label>Remittance Fee (₹)</Label>
                  <Input type="number" step="0.01" value={form.remittanceFee} onChange={e => set('remittanceFee', e.target.value)} className="mt-1" data-testid="expense-remittance-fee-input" placeholder="e.g. 500" />
                </div>
                {showFxPreview && (
                  <div className="col-span-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg p-2" data-testid="expense-fx-preview">
                    Total INR Debited ≈ <span className="font-mono font-semibold">₹{totalInrDebitedPreview.toLocaleString('en-IN')}</span> = {form.foreignCurrency || 'FX'} {foreignAmt.toLocaleString()} × {fxRate} + ₹{remittanceFee.toLocaleString()} fee. This overrides Paid Amount on save (fee always shown separately, never folded in silently).
                  </div>
                )}
              </div>
            </div>

            {/* Vendor Payment Gateway Charges (2026-09) — domestic card/NEFT/IMPS fee */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1.5 mb-2">
                <CreditCard className="h-3.5 w-3.5" /> Vendor Payment Method (domestic card/NEFT/IMPS fee)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Select value={form.vendorPaymentMethod || 'none'} onValueChange={v => set('vendorPaymentMethod', v === 'none' ? '' : v)}>
                    <SelectTrigger className="mt-1" data-testid="expense-vendor-payment-method-select"><SelectValue placeholder="Not applicable" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not applicable</SelectItem>
                      {VENDOR_PAYMENT_METHODS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1">Fee % comes from the selected vendor's configured rates (Vendor → Registration dialog) — enter the Gross Payment above in "Paid Amount".</p>
                </div>
                {showMethodPreview && (
                  <div className="col-span-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg p-2" data-testid="expense-method-fee-preview">
                    Gross Payment <span className="font-mono font-semibold">₹{grossPayment.toLocaleString('en-IN')}</span> + Fee ({methodFeePct}%) <span className="font-mono font-semibold">₹{methodFeePreview.toLocaleString('en-IN')}</span> = Total Debited <span className="font-mono font-semibold">₹{totalDebitedMethodPreview.toLocaleString('en-IN')}</span>. This overrides Paid Amount on save (fee always shown separately, never folded in silently).
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-expense-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {saving ? 'Saving...' : editing ? 'Update' : 'Record'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingExpenses;
